import React, { useCallback, useEffect, useState } from 'react';
import Modal from './common/Modal';
import { toast } from './common/Toast';
import { usePermission } from '../hooks/usePermission';
import * as apartmentsApi from '../api/apartments';
import { Apartment, Role, User } from '../types';

interface PromoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: User | null;
  onSubmit: (role: Role, apartmentIds: string[]) => Promise<void>;
}

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'super_admin', label: '최상위 관리자 (super_admin)' },
  { value: 'admin', label: '관리자 (admin)' },
  { value: 'manager', label: '매니저 (manager)' },
];

const PromoteModal: React.FC<PromoteModalProps> = ({ isOpen, onClose, target, onSubmit }) => {
  const { canPromoteTo } = usePermission();
  const [role, setRole] = useState<Role>('manager');
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const availableOptions = ROLE_OPTIONS.filter((o) => canPromoteTo(o.value));

  useEffect(() => {
    if (!isOpen) return;
    if (availableOptions.length > 0) {
      const defaultRole = availableOptions.find((o) => o.value === 'manager')?.value
        ?? availableOptions[0].value;
      setRole(defaultRole);
    }
    setSelected([]);
    setLoading(true);
    apartmentsApi
      .getList({ page: 1, limit: 100 })
      .then((res) => setApartments(res.items))
      .catch(() => toast.error('장소 목록을 불러올 수 없습니다'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const handleConfirm = async () => {
    if (!target) return;
    if ((role === 'admin' || role === 'manager') && selected.length === 0) {
      if (!window.confirm('담당 장소를 선택하지 않았습니다. 그대로 진행하시겠습니까?')) return;
    }
    try {
      setSubmitting(true);
      await onSubmit(role, role === 'super_admin' ? [] : selected);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const needsApartments = role === 'admin' || role === 'manager';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={target ? `${target.nickname} (${target.phone}) 승격` : '승격'}
      onConfirm={handleConfirm}
      confirmText={submitting ? '처리 중...' : '승격'}
    >
      {availableOptions.length === 0 ? (
        <p>승격 가능한 역할이 없습니다.</p>
      ) : (
        <>
          <div className="form-group">
            <label>역할</label>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {availableOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {needsApartments && (
            <div className="form-group">
              <label>담당 장소 {role === 'admin' ? '(admin)' : '(manager)'}</label>
              {loading ? (
                <p>불러오는 중...</p>
              ) : apartments.length === 0 ? (
                <p>등록된 장소가 없습니다.</p>
              ) : (
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    padding: 12,
                    borderRadius: 4,
                  }}
                >
                  {apartments.map((apt) => (
                    <label
                      key={apt.id}
                      style={{ display: 'flex', alignItems: 'center', padding: '4px 0', gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(apt.id)}
                        onChange={() => toggle(apt.id)}
                      />
                      <span>{apt.name}</span>
                      <span style={{ color: '#999', fontSize: 12 }}>{apt.address}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          {role === 'super_admin' && (
            <p style={{ fontSize: 12, color: '#d2691e' }}>
              super_admin은 모든 장소/기능에 접근 가능하며 담당 장소 지정이 필요하지 않습니다.
            </p>
          )}
        </>
      )}
    </Modal>
  );
};

export default PromoteModal;
